import { useState } from 'react'
import { Modal } from '@/components/ui/modal'
import { StatusBadge } from '@/components/ui/status-badge'
import { useChangeTier, usePauseAccount, useResumeAccount } from '@/hooks/use-accounts'
import { useAuth } from '@/lib/auth-context'
import type { AccountDetailResponse } from '@/lib/types'

const TIERS = ['free', 'starter', 'pro', 'growth', 'scale'] as const

interface OverviewTabProps {
  account: AccountDetailResponse
}

export function OverviewTab({ account }: OverviewTabProps): React.JSX.Element {
  const { isViewer } = useAuth()
  const pauseMutation = usePauseAccount()
  const resumeMutation = useResumeAccount()
  const changeTierMutation = useChangeTier()

  const [showPauseModal, setShowPauseModal] = useState(false)
  const [showTierModal, setShowTierModal] = useState(false)
  const [pauseReason, setPauseReason] = useState('')
  const [selectedTier, setSelectedTier] = useState(account.tier)

  function handlePause(): void {
    if (!pauseReason.trim()) return
    pauseMutation.mutate(
      { id: account.id, reason: pauseReason },
      {
        onSuccess: () => {
          setShowPauseModal(false)
          setPauseReason('')
        },
      },
    )
  }

  function handleResume(): void {
    resumeMutation.mutate(account.id)
  }

  function handleChangeTier(): void {
    if (selectedTier === account.tier) return
    changeTierMutation.mutate(
      { id: account.id, tier: selectedTier },
      {
        onSuccess: () => setShowTierModal(false),
      },
    )
  }

  return (
    <div className="space-y-6">
      {/* Account info */}
      <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
        <h3 className="mb-4 text-sm font-medium text-text-primary">Account Information</h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <InfoField label="Tier" value={<StatusBadge status={account.tier} />} />
          <InfoField
            label="Status"
            value={<StatusBadge status={account.sending_paused ? 'paused' : account.status} />}
          />
          <InfoField label="Created" value={new Date(account.created_at).toLocaleDateString()} />
          <InfoField
            label="Last Active"
            value={
              account.last_active_at
                ? new Date(account.last_active_at).toLocaleDateString()
                : 'Never'
            }
          />
        </div>
        {account.sending_paused && account.pause_reason && (
          <div className="mt-4 rounded-lg bg-yellow-50 p-3">
            <p className="text-sm text-yellow-800">
              <span className="font-medium">Pause reason:</span> {account.pause_reason}
            </p>
          </div>
        )}
      </div>

      {/* Actions */}
      {!isViewer && (
        <div className="flex flex-wrap gap-3">
          {account.sending_paused ? (
            <button
              type="button"
              onClick={handleResume}
              disabled={resumeMutation.isPending}
              className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
            >
              {resumeMutation.isPending ? 'Resuming...' : 'Resume Sending'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setShowPauseModal(true)}
              className="rounded-lg bg-yellow-600 px-4 py-2 text-sm font-medium text-white hover:bg-yellow-700"
            >
              Pause Sending
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setSelectedTier(account.tier)
              setShowTierModal(true)
            }}
            className="rounded-lg border border-surface-3 bg-surface-0 px-4 py-2 text-sm font-medium text-text-primary hover:bg-surface-1"
          >
            Change Tier
          </button>
        </div>
      )}

      {/* Pause confirmation modal */}
      <Modal
        open={showPauseModal}
        onClose={() => setShowPauseModal(false)}
        title="Pause Account Sending"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-text-secondary">
            This will immediately stop all email sending for{' '}
            <span className="font-medium text-text-primary">{account.user_email}</span>.
          </p>
          <div>
            <label htmlFor="pause-reason" className="block text-sm font-medium text-text-primary">
              Reason
            </label>
            <textarea
              id="pause-reason"
              value={pauseReason}
              onChange={(e) => setPauseReason(e.target.value)}
              placeholder="Why is this account being paused?"
              className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 p-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-admin-500 focus:outline-none"
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowPauseModal(false)}
              className="rounded-lg border border-surface-3 px-4 py-2 text-sm text-text-secondary hover:bg-surface-1"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handlePause}
              disabled={!pauseReason.trim() || pauseMutation.isPending}
              className="rounded-lg bg-yellow-600 px-4 py-2 text-sm font-medium text-white hover:bg-yellow-700 disabled:opacity-50"
            >
              {pauseMutation.isPending ? 'Pausing...' : 'Pause Account'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Change tier modal */}
      <Modal
        open={showTierModal}
        onClose={() => setShowTierModal(false)}
        title="Change Account Tier"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-text-secondary">
            Current tier: <StatusBadge status={account.tier} />
          </p>
          <div>
            <label htmlFor="new-tier" className="block text-sm font-medium text-text-primary">
              New Tier
            </label>
            <select
              id="new-tier"
              value={selectedTier}
              onChange={(e) => setSelectedTier(e.target.value)}
              className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 p-2 text-sm text-text-primary focus:border-admin-500 focus:outline-none"
            >
              {TIERS.map((t) => (
                <option key={t} value={t}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowTierModal(false)}
              className="rounded-lg border border-surface-3 px-4 py-2 text-sm text-text-secondary hover:bg-surface-1"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleChangeTier}
              disabled={selectedTier === account.tier || changeTierMutation.isPending}
              className="rounded-lg bg-admin-600 px-4 py-2 text-sm font-medium text-white hover:bg-admin-700 disabled:opacity-50"
            >
              {changeTierMutation.isPending ? 'Changing...' : 'Change Tier'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

function InfoField({ label, value }: { label: string; value: React.ReactNode }): React.JSX.Element {
  return (
    <div>
      <p className="text-xs text-text-tertiary">{label}</p>
      <div className="mt-1 text-sm text-text-primary">{value}</div>
    </div>
  )
}
